import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calculator, MapPin, Ruler, Home as HomeIcon, Layers, Sparkles,
  CheckCircle2, ArrowRight, Info, Building2, Loader2, UserCircle2,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";

const CITIES = [
  { name: "Bangalore", tier: 1 },
  { name: "Mumbai", tier: 1 },
  { name: "Delhi NCR", tier: 1 },
  { name: "Hyderabad", tier: 2 },
  { name: "Chennai", tier: 2 },
  { name: "Pune", tier: 2 },
  { name: "Kolkata", tier: 2 },
  { name: "Ahmedabad", tier: 2 },
  { name: "Jaipur", tier: 3 },
  { name: "Lucknow", tier: 3 },
  { name: "Indore", tier: 3 },
  { name: "Coimbatore", tier: 3 },
  { name: "Chandigarh", tier: 3 },
  { name: "Patna", tier: 3 },
  { name: "Gaya", tier: 3 },
  { name: "Muzaffarpur", tier: 3 },
  { name: "Bhagalpur", tier: 3 },
  { name: "Darbhanga", tier: 3 },
  { name: "Other", tier: 3 },
];

const TIER_BASE_RATE = { 1: 1850, 2: 1750, 3: 1650 };

const PACKAGES = [
  {
    id: "classic",
    name: "Classic",
    multiplier: 1.0,
    tagline: "Solid, no-nonsense build",
    highlights: ["RCC frame structure", "Branded vitrified flooring", "Standard CP & sanitary", "Acrylic emulsion paint"],
  },
  {
    id: "premium",
    name: "Premium",
    multiplier: 1.2,
    tagline: "Most popular choice",
    highlights: ["Premium fittings (Jaquar / Hindware)", "Designer wall finishes", "Modular kitchen included", "Smart electrical points"],
    popular: true,
  },
  {
    id: "luxury",
    name: "Luxury",
    multiplier: 1.5,
    tagline: "Top-tier finishes",
    highlights: ["Imported tiles & marble", "Premium German fittings", "Home automation ready", "Designer false ceiling"],
  },
];

const BHK_OPTIONS = [
  { id: "1bhk", label: "1 BHK", bedrooms: 1, bathrooms: 1 },
  { id: "2bhk", label: "2 BHK", bedrooms: 2, bathrooms: 2 },
  { id: "3bhk", label: "3 BHK", bedrooms: 3, bathrooms: 3 },
  { id: "4bhk", label: "4 BHK", bedrooms: 4, bathrooms: 4 },
  { id: "5bhk", label: "5+ BHK", bedrooms: 5, bathrooms: 5 },
];

// Add-on rate cards. `compute(ctx)` receives { L, W, F, builtUp, pkgId } and
// returns the rupee amount; `note` is shown under the label for context.
const ADDONS = [
  { id: "compound_wall", label: "Compound wall", note: "2 × (L+W) × ₹1000 + ₹50,000",
    compute: ({ L, W }) => 2 * (L + W) * 1000 + 50000 },
  { id: "parking_shed", label: "Covered car parking", note: "Steel + polycarbonate",
    compute: () => 80000 },
  { id: "lift", label: "Passenger lift", note: "5-stop, branded",
    compute: () => 650000 },
  { id: "solar", label: "Solar (3 KW)", note: "On-grid, 25-yr warranty · without subsidy",
    compute: () => 220000 },
  { id: "ug_tank", label: "Underground water tank", note: "10,000 L RCC",
    compute: () => 180000 },
  { id: "modular_kitchen", label: "Modular kitchen upgrade", note: "L-shape, soft-close",
    compute: () => 180000 },
  { id: "false_ceiling", label: "False ceiling (full home)", note: "L × W × F × {Classic 100 / Premium 160 / Luxury 250}",
    compute: ({ L, W, F, pkgId }) => {
      const rate = pkgId === "luxury" ? 250 : pkgId === "premium" ? 160 : 100;
      return L * W * F * rate;
    } },
  { id: "premium_flooring", label: "Imported marble flooring", note: "L × W × F × {Classic 900 / Premium 2500 / Luxury 3500}",
    compute: ({ L, W, F, pkgId }) => {
      const rate = pkgId === "luxury" ? 3500 : pkgId === "premium" ? 2500 : 900;
      return L * W * F * rate;
    } },
  { id: "smart_home", label: "Smart home automation", note: "₹1,00,000 + L × W × F × ₹500",
    compute: ({ L, W, F }) => 100000 + L * W * F * 500 },
  { id: "landscaping", label: "Landscaping & lawn", note: "Front yard · for 1000 sqft",
    compute: () => 95000 },
];

// Cost breakdown — each row carries a list of sub-items (rate-card details) so
// the user can expand the row and see what's inside, similar to Brick & Bolt.
const BREAKDOWN_RATIOS = [
  { id: "structure", label: "Structure & masonry", pct: 0.38, color: "from-emerald-400 to-emerald-600",
    items: ["RCC frame (M25 / Fe-500)", "Brickwork & plastering", "Roof slab + waterproofing", "Lintels, sunshades, parapet"] },
  { id: "finishing", label: "Finishing & flooring", pct: 0.30, color: "from-sky-400 to-sky-600",
    items: ["Vitrified flooring (living, bedrooms)", "Anti-skid tiles (kitchen, bath)", "Wall & ceiling putty + paint", "Granite kitchen platform"] },
  { id: "mep", label: "Plumbing, electrical & HVAC", pct: 0.22, color: "from-amber-400 to-amber-600",
    items: ["CPVC plumbing + sanitaryware", "Concealed copper wiring", "Modular switches, MCB panel", "AC pre-piping (per package)"] },
  { id: "aesthetics", label: "Doors, paint, fixtures", pct: 0.10, color: "from-rose-400 to-rose-500",
    items: ["Main door (teak veneer)", "Flush doors with hardware", "UPVC / aluminium windows", "Loft, wardrobes (basic)"] },
];

const BUILDABLE_FACTOR = 0.78;

const fmtINR = (n) => {
  if (!Number.isFinite(n)) return "—";
  if (n >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
  if (n >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
  return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

const fmtINRFull = (n) => `₹${Math.round(n).toLocaleString("en-IN")}`;

export default function CostCalculator() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [city, setCity] = useState("Bangalore");
  const [expandedBreakdown, setExpandedBreakdown] = useState({});
  const [length, setLength] = useState(40);
  const [width, setWidth] = useState(30);
  const [floors, setFloors] = useState(2);
  const [bhk, setBhk] = useState("3bhk");
  const [pkg, setPkg] = useState("premium");
  const [addons, setAddons] = useState({});
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadForm, setLeadForm] = useState({ name: "", mobile: "", email: "" });
  const [leadState, setLeadState] = useState({ loading: false, done: false, error: "" });

  const calc = useMemo(() => {
    const tier = CITIES.find((c) => c.name === city)?.tier ?? 2;
    const pkgObj = PACKAGES.find((p) => p.id === pkg) || PACKAGES[1];
    const L = Number(length) || 0;
    const W = Number(width) || 0;
    const F = Math.max(1, Number(floors) || 1);
    const plotArea = L * W;
    const builtUp = plotArea * F * BUILDABLE_FACTOR;
    const ratePerSqft = TIER_BASE_RATE[tier] * pkgObj.multiplier;
    const construction = builtUp * ratePerSqft;
    const ctx = { L, W, F, builtUp, pkgId: pkg };
    const addonItems = ADDONS.filter((a) => addons[a.id]).map((a) => ({
      id: a.id,
      label: a.label,
      amount: a.compute(ctx),
    }));
    const addonTotal = addonItems.reduce((s, a) => s + a.amount, 0);
    const subtotal = construction + addonTotal;
    const gst = subtotal * 0.05;
    const total = subtotal + gst;
    const perSqft = builtUp > 0 ? construction / builtUp : 0;
    return {
      tier, pkgObj, plotArea, builtUp, ratePerSqft, construction,
      addonItems, addonTotal, subtotal, gst, total, perSqft,
    };
  }, [city, length, width, floors, pkg, addons]);

  const toggleAddon = (id) => setAddons((a) => ({ ...a, [id]: !a[id] }));

  const submitLead = async (e) => {
    e.preventDefault();
    if (!leadForm.name || !leadForm.mobile) {
      setLeadState({ loading: false, done: false, error: "Name and mobile are required" });
      return;
    }
    setLeadState({ loading: true, done: false, error: "" });
    try {
      const bhkObj = BHK_OPTIONS.find((b) => b.id === bhk);
      await api.post("/leads", {
        name: leadForm.name,
        mobile: leadForm.mobile,
        email: leadForm.email || null,
        source: "cost_calculator",
        plot_details: { length: String(length), width: String(width) },
        message: [
          `Construction cost estimate request`,
          `City: ${city}`,
          `Plot: ${length} × ${width} ft (${calc.plotArea} sqft)`,
          `Floors: ${floors}, Built-up: ${Math.round(calc.builtUp)} sqft`,
          `Config: ${bhkObj?.label}, Package: ${calc.pkgObj.name}`,
          `Add-ons: ${calc.addonItems.map((a) => a.label).join(", ") || "none"}`,
          `Estimated total: ${fmtINRFull(calc.total)}`,
        ].join("\n"),
      });
      setLeadState({ loading: false, done: true, error: "" });
    } catch (err) {
      setLeadState({
        loading: false,
        done: false,
        error: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    }
  };

  return (
    <div data-testid="cost-calculator-page" className="bg-gradient-to-b from-emerald-50/30 to-white">
      {/* Header */}
      <section className="nc-container pt-10 pb-6">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 text-emerald-700 text-xs font-semibold uppercase tracking-[0.2em]">
            <Calculator className="w-4 h-4" /> Cost Estimator
          </div>
          {user?.name && (
            <div className="flex items-center gap-1.5 text-sm text-slate-600" data-testid="calc-loggedin-user">
              <UserCircle2 className="w-4 h-4 text-emerald-600" />
              <span>Signed in as <strong className="text-slate-900">{user.name}</strong></span>
            </div>
          )}
        </div>
        <h1 className="mt-3 text-3xl md:text-5xl font-semibold tracking-tight text-slate-900 leading-[1.1]">
          What will <span className="nc-gradient-text">your home</span> cost?
        </h1>
        <p className="mt-3 text-slate-600 max-w-2xl">
          Get a transparent ballpark in under a minute. Adjust the inputs on the left and watch the estimate update in real time. No sign-up required to see the number.
        </p>
      </section>

      <section className="nc-container pb-16">
        <div className="grid lg:grid-cols-12 gap-8">
          {/* Inputs */}
          <div className="lg:col-span-7 space-y-6">
            {/* Location */}
            <Card icon={MapPin} title="Where are you building?" step="1">
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                {CITIES.map((c) => (
                  <button
                    key={c.name}
                    type="button"
                    onClick={() => setCity(c.name)}
                    data-testid={`calc-city-${c.name.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-2.5 rounded-xl text-sm font-medium border transition text-left ${
                      city === c.name
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800 ring-2 ring-emerald-100"
                        : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300"
                    }`}
                  >
                    <div>{c.name}</div>
                    <div className="text-[10px] text-slate-400 mt-0.5">Tier {c.tier}</div>
                  </button>
                ))}
              </div>
            </Card>

            {/* Plot & floors */}
            <Card icon={Ruler} title="Plot & floors" step="2">
              <div className="grid grid-cols-2 gap-3">
                <Field label="Plot length (ft)">
                  <input
                    type="number" min="10" className="nc-input"
                    value={length} onChange={(e) => setLength(e.target.value)}
                    data-testid="calc-length"
                  />
                </Field>
                <Field label="Plot width (ft)">
                  <input
                    type="number" min="10" className="nc-input"
                    value={width} onChange={(e) => setWidth(e.target.value)}
                    data-testid="calc-width"
                  />
                </Field>
              </div>
              <div className="mt-3 text-xs text-slate-500 flex items-center gap-1.5">
                <Info className="w-3.5 h-3.5" />
                Plot area: <strong className="text-slate-700">{calc.plotArea.toLocaleString("en-IN")} sqft</strong>
                <span className="mx-1">·</span>
                Buildable (G+{floors - 1}): <strong className="text-slate-700">{Math.round(calc.builtUp).toLocaleString("en-IN")} sqft</strong>
              </div>

              <div className="mt-5">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">Number of floors</div>
                <div className="flex flex-wrap gap-2">
                  {[1, 2, 3, 4].map((f) => (
                    <button
                      key={f}
                      type="button"
                      onClick={() => setFloors(f)}
                      data-testid={`calc-floors-${f}`}
                      className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${
                        floors === f
                          ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                          : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300"
                      }`}
                    >
                      G{f > 1 ? `+${f - 1}` : " (single)"}
                    </button>
                  ))}
                </div>
              </div>
            </Card>

            {/* BHK */}
            <Card icon={HomeIcon} title="Configuration" step="3">
              <div className="flex flex-wrap gap-2">
                {BHK_OPTIONS.map((b) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => setBhk(b.id)}
                    data-testid={`calc-bhk-${b.id}`}
                    className={`px-4 py-2.5 rounded-xl text-sm font-medium border transition ${
                      bhk === b.id
                        ? "border-emerald-500 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 bg-white text-slate-700 hover:border-emerald-300"
                    }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>
              <p className="mt-3 text-xs text-slate-500">
                Configuration helps us recommend the right plan template — it doesn't change the per-sqft rate, but very small homes (under {Math.max(600, BHK_OPTIONS.find((b) => b.id === bhk)?.bedrooms * 350)} sqft for {BHK_OPTIONS.find((b) => b.id === bhk)?.label}) may not be feasible.
              </p>
            </Card>

            {/* Package */}
            <Card icon={Layers} title="Construction package" step="4">
              <div className="grid sm:grid-cols-3 gap-3">
                {PACKAGES.map((p) => {
                  const active = pkg === p.id;
                  return (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setPkg(p.id)}
                      data-testid={`calc-pkg-${p.id}`}
                      className={`relative text-left rounded-2xl border-2 p-4 transition ${
                        active
                          ? "border-emerald-500 bg-gradient-to-br from-emerald-50 to-sky-50"
                          : "border-slate-200 bg-white hover:border-emerald-300"
                      }`}
                    >
                      {p.popular && (
                        <span className="absolute -top-2.5 right-3 nc-chip text-[10px] !py-0.5">
                          Most picked
                        </span>
                      )}
                      <div className="flex items-baseline justify-between">
                        <div className="font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{p.name}</div>
                        {active && <CheckCircle2 className="w-4 h-4 text-emerald-600" />}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5">{p.tagline}</div>
                      <div className="mt-3 text-2xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
                        ₹{Math.round(TIER_BASE_RATE[calc.tier] * p.multiplier).toLocaleString("en-IN")}
                        <span className="text-xs font-normal text-slate-500"> /sqft</span>
                      </div>
                      <ul className="mt-3 space-y-1.5 text-xs text-slate-600">
                        {p.highlights.map((h) => (
                          <li key={h} className="flex items-start gap-1.5">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 mt-0.5 flex-shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Add-ons */}
            <Card icon={Sparkles} title="Add-ons (optional)" step="5">
              <div className="grid sm:grid-cols-2 gap-2">
                {ADDONS.map((a) => {
                  const on = !!addons[a.id];
                  const amt = a.compute({
                    L: Number(length) || 0,
                    W: Number(width) || 0,
                    F: Math.max(1, Number(floors) || 1),
                    builtUp: calc.builtUp,
                    pkgId: pkg,
                  });
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => toggleAddon(a.id)}
                      data-testid={`calc-addon-${a.id}`}
                      className={`flex items-start justify-between gap-3 px-4 py-3 rounded-xl border-2 text-left transition ${
                        on
                          ? "border-emerald-500 bg-emerald-50/60"
                          : "border-slate-200 bg-white hover:border-emerald-300"
                      }`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold text-slate-900">{a.label}</div>
                        <div className="text-[11px] text-slate-500">{a.note}</div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <div className="text-sm font-semibold text-slate-900">+{fmtINR(amt)}</div>
                        <div className={`mt-1 inline-block w-9 h-5 rounded-full transition relative ${on ? "bg-emerald-500" : "bg-slate-300"}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition ${on ? "left-4" : "left-0.5"}`} />
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>

          {/* Estimate panel */}
          <aside className="lg:col-span-5">
            <div className="sticky top-20 space-y-4">
              <div className="bg-white rounded-3xl border border-slate-200 nc-soft-shadow overflow-hidden">
                <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6">
                  <div className="text-xs uppercase tracking-[0.2em] text-emerald-300 font-semibold">Your estimate</div>
                  <div className="mt-2 text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "Poppins" }}>
                    {fmtINR(calc.total)}
                  </div>
                  <div className="mt-1 text-sm text-slate-300">
                    incl. 5% GST · {fmtINRFull(Math.round(calc.perSqft))}/sqft construction
                  </div>
                  <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
                    <Mini label="Built-up" value={`${Math.round(calc.builtUp).toLocaleString("en-IN")} sqft`} />
                    <Mini label="City tier" value={`Tier ${calc.tier}`} />
                    <Mini label="Package" value={calc.pkgObj.name} />
                  </div>
                </div>

                <div className="p-6">
                  <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Cost breakdown</div>
                  <div className="space-y-3">
                    {BREAKDOWN_RATIOS.map((b) => {
                      const amt = calc.construction * b.pct;
                      const open = !!expandedBreakdown[b.id];
                      return (
                        <div key={b.id}>
                          <button
                            type="button"
                            onClick={() => setExpandedBreakdown(s => ({ ...s, [b.id]: !s[b.id] }))}
                            className="w-full flex items-center justify-between text-sm group"
                            data-testid={`calc-breakdown-${b.id}-toggle`}
                          >
                            <span className="text-slate-700 inline-flex items-center gap-1.5">
                              <span className="w-4 h-4 inline-flex items-center justify-center rounded border border-slate-300 text-slate-500 group-hover:border-emerald-400 group-hover:text-emerald-600 transition text-[11px] leading-none">
                                {open ? "−" : "+"}
                              </span>
                              {b.label}
                            </span>
                            <span className="font-semibold text-slate-900">{fmtINR(amt)}</span>
                          </button>
                          <div className="mt-1 h-1.5 rounded-full bg-slate-100 overflow-hidden">
                            <div className={`h-full bg-gradient-to-r ${b.color}`} style={{ width: `${b.pct * 100}%` }} />
                          </div>
                          {open && (
                            <ul className="mt-2 ml-5 space-y-1 text-xs text-slate-600 list-disc list-inside" data-testid={`calc-breakdown-${b.id}-items`}>
                              {b.items.map((it) => <li key={it}>{it}</li>)}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {calc.addonItems.length > 0 && (
                    <>
                      <div className="mt-5 pt-5 border-t border-slate-100 text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">
                        Add-ons
                      </div>
                      <div className="space-y-2">
                        {calc.addonItems.map((a) => (
                          <div key={a.id} className="flex items-center justify-between text-sm">
                            <span className="text-slate-700">{a.label}</span>
                            <span className="font-semibold text-slate-900">{fmtINR(a.amount)}</span>
                          </div>
                        ))}
                      </div>
                    </>
                  )}

                  <div className="mt-5 pt-5 border-t border-slate-100 space-y-2 text-sm">
                    <Row label="Construction" value={fmtINRFull(calc.construction)} />
                    {calc.addonTotal > 0 && <Row label="Add-ons" value={fmtINRFull(calc.addonTotal)} />}
                    <Row label="GST (5%)" value={fmtINRFull(calc.gst)} muted />
                    <Row label="Total" value={fmtINRFull(calc.total)} bold />
                  </div>

                  <button
                    type="button"
                    onClick={() => setLeadOpen(true)}
                    className="mt-6 nc-btn-primary w-full justify-center"
                    data-testid="calc-get-quote-btn"
                  >
                    Get a detailed quote <ArrowRight className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => nav("/plans")}
                    className="mt-2 nc-btn-secondary w-full justify-center"
                    data-testid="calc-browse-plans-btn"
                  >
                    <Building2 className="w-4 h-4" /> Browse matching plans
                  </button>
                  <p className="mt-3 text-[11px] text-slate-400 leading-relaxed">
                    Estimates are indicative and may vary by ±10% based on soil conditions, site access, and material market rates at the time of contract.
                  </p>
                </div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* Lead modal */}
      {leadOpen && (
        <div
          className="fixed inset-0 z-[60] bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !leadState.loading && setLeadOpen(false)}
          data-testid="calc-lead-modal"
        >
          <div
            className="bg-white rounded-3xl max-w-md w-full p-6 md:p-8 nc-soft-shadow"
            onClick={(e) => e.stopPropagation()}
          >
            {leadState.done ? (
              <div className="text-center py-4">
                <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
                  <CheckCircle2 className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="mt-4 text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
                  We'll be in touch, {leadForm.name}!
                </h3>
                <p className="mt-2 text-slate-600 text-sm">
                  Our build advisor will call within 24 hours with a detailed BOQ for your <strong>{fmtINR(calc.total)}</strong> estimate.
                </p>
                <button
                  onClick={() => setLeadOpen(false)}
                  className="mt-6 nc-btn-primary w-full justify-center"
                >
                  Done
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>
                  Lock in this estimate
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Share your details and we'll send a detailed BOQ + connect you with a vetted contractor in {city}.
                </p>
                <form onSubmit={submitLead} className="mt-5 space-y-3">
                  <Field label="Full name">
                    <input
                      className="nc-input"
                      value={leadForm.name}
                      onChange={(e) => setLeadForm({ ...leadForm, name: e.target.value })}
                      data-testid="calc-lead-name"
                      required
                    />
                  </Field>
                  <Field label="Mobile (10 digits)">
                    <input
                      className="nc-input"
                      pattern="[0-9]{10}" maxLength="10"
                      value={leadForm.mobile}
                      onChange={(e) => setLeadForm({ ...leadForm, mobile: e.target.value })}
                      data-testid="calc-lead-mobile"
                      required
                    />
                  </Field>
                  <Field label="Email (optional)">
                    <input
                      type="email"
                      className="nc-input"
                      value={leadForm.email}
                      onChange={(e) => setLeadForm({ ...leadForm, email: e.target.value })}
                      data-testid="calc-lead-email"
                    />
                  </Field>
                  {leadState.error && <p className="text-sm text-rose-600">{leadState.error}</p>}
                  <div className="rounded-xl bg-slate-50 border border-slate-100 p-3 text-xs text-slate-600">
                    <div className="flex items-center justify-between">
                      <span>Your estimate</span>
                      <strong className="text-slate-900">{fmtINR(calc.total)}</strong>
                    </div>
                    <div className="mt-1 flex items-center justify-between">
                      <span>{calc.pkgObj.name} · {Math.round(calc.builtUp)} sqft · {city}</span>
                    </div>
                  </div>
                  <button
                    type="submit"
                    disabled={leadState.loading}
                    className="nc-btn-primary w-full justify-center"
                    data-testid="calc-lead-submit"
                  >
                    {leadState.loading && <Loader2 className="w-4 h-4 animate-spin" />}
                    Send my detailed quote
                  </button>
                  <button
                    type="button"
                    onClick={() => setLeadOpen(false)}
                    className="text-xs text-slate-500 hover:text-slate-700 w-full text-center"
                  >
                    Maybe later
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ icon: Icon, title, step, children }) {
  return (
    <div className="bg-white rounded-2xl border border-slate-200 nc-soft-shadow p-5 md:p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-400 font-semibold">Step {step}</div>
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: "Poppins" }}>{title}</h2>
        </div>
      </div>
      {children}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-600 uppercase tracking-wider">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value, bold, muted }) {
  return (
    <div className="flex items-center justify-between">
      <span className={muted ? "text-slate-500" : "text-slate-700"}>{label}</span>
      <span className={`${bold ? "text-lg font-semibold text-emerald-700" : "font-semibold text-slate-900"}`}>{value}</span>
    </div>
  );
}

function Mini({ label, value }) {
  return (
    <div className="bg-white/10 rounded-lg px-2.5 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-slate-400">{label}</div>
      <div className="text-xs font-semibold text-white truncate">{value}</div>
    </div>
  );
}
